/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
  Address,
  AddressPurpose,
  CountryRegionInfo,
  StateProvinceInfo,
} from "@msdyn365-commerce/retail-proxy";
import {
  getTelemetryObject,
  IModuleProps,
  ITelemetryContent,
} from "@msdyn365-commerce-modules/utilities";
import classnames from "classnames";
import { observable, set } from "mobx";
import * as React from "react";

import { AddressCommon } from "@msdyn365-commerce-modules/address";
import { AddressFormat } from "@msdyn365-commerce-modules/address";
import {
  AddressItemDisplayType,
  AddressItemType,
} from "@msdyn365-commerce-modules/address";
import { AddressMetaData } from "@msdyn365-commerce-modules/address";
import {
  AddressOperation,
  AddressType,
  IAddressResource,
  IAddressResponse,
} from "@msdyn365-commerce-modules/address";
import {
  AddressAddUpdate,
  IAddressAddUpdateProps,
} from "@msdyn365-commerce-modules/address";
import { IBritaxBusinessAccountAddressData } from "./britax-business-account-address.data";
import { IBritaxBusinessAccountAddressProps } from "./britax-business-account-address.props.autogenerated";

export interface IBusinessAccountAddressExtendedProps
  extends IBritaxBusinessAccountAddressProps<IBritaxBusinessAccountAddressData> {
  hasError: boolean;
  isRequired: boolean;
  resetAddress: boolean;
  updateForm(errors: boolean, currentAddress?: Address): void;
}

export interface IBusinessAccountAddressState {
  countryId?: string;
  states?: StateProvinceInfo[];
}

export interface IBusinessAccountAddressViewProps {
  className: string;
  currentOperation: AddressOperation;
  selectedAddress?: Address;
  addUpdateAddress: Address;
  addressListSelectedAddress: Address;
  countryRegionId: string;
  stateProvinceInfo?: StateProvinceInfo[];
  customerAddresses: Address[];
  validationError: object;
  addressActionResponse?: IAddressResponse;
  BusinessAccountAddress: IModuleProps;
  isUpdating: boolean;
  hasError: boolean;
  showAddOrUpdateAddress: IAddressAddUpdateProps;
}

/**
 *
 * Address component.
 * @extends {React.Component<IBritaxBusinessAccountAddressProps<IBritaxBusinessAccountAddressData>, IBusinessAccountAddressState>}
 */
class BusinessAccountAddress extends React.Component<
  IBusinessAccountAddressExtendedProps,
  IBusinessAccountAddressState
> {
  @observable private addUpdateAddress: Address;

  @observable private countryRegionId: string = "DEU";

  @observable private stateProvinceInfo?: StateProvinceInfo[];

  @observable private readonly validationError: object;

  private readonly addressActionResponse?: IAddressResponse;

  private readonly isUpdating?: boolean;

  private readonly addressCommon: AddressCommon;

  private addressFormat: AddressFormat;

  private readonly countryRegions: CountryRegionInfo[];

  private readonly addressPurposes: AddressPurpose[];

  private readonly resources: IAddressResource;

  // VSI-Customization code upgrade 10.0.32 Talha ADO:2621
  private readonly defaultAddressType: number = 0; // Default to None (This value set in HQ as Business)

  private readonly excludedList: AddressItemType[] = [
    AddressItemType.Name,
    AddressItemType.AddressTypeValue,
    AddressItemType.IsPrimary,
  ]; // Default to hiding Name and AddressType inputs

  private readonly telemetryContent: ITelemetryContent;

  constructor(props: IBusinessAccountAddressExtendedProps) {
    super(props);
    const { context, data, resources, telemetry } = this.props;
    this.addUpdateAddress = {};
    this.resources = resources;
    this.countryRegions = data.countryRegions.result || [];
    this.addressPurposes = data.addressPurposes.result || [];
    this.stateProvinceInfo = data.countryStates.result || [];
    this.addressFormat = new AddressFormat(
      this.countryRegions,
      new AddressMetaData(
        { ...resources },
        this.excludedList,
        props.isRequired ? undefined : []
      ),
      this.addressPurposes
    );
    this.addressCommon = new AddressCommon(context, resources, telemetry);
    this.validationError = {};
    this.telemetryContent = getTelemetryObject(
      this.props.context.request.telemetryPageName!,
      this.props.friendlyName,
      this.props.telemetry
    );
  }

  public componentDidMount(): void {
    this._setDefaultCountryRegionId();
  }

  public shouldComponentUpdate(
    nextProps: IBusinessAccountAddressExtendedProps,
    nextState: IBusinessAccountAddressState
  ): boolean {
    if (this.state === nextState && this.props.data === nextProps.data) {
      return false;
    }
    return true;
  }

  public render(): JSX.Element | null {
    const { renderView, config, hasError, resetAddress } = this.props;

    // Business-sign-up will pass this flag when cancel button is clicked
    if (resetAddress) {
      this._resetAddressFields();
    }

    const viewProps = {
      ...this.props,
      countryRegionId: this.countryRegionId,
      stateProvinceInfo: this.stateProvinceInfo,
      validationError: this.validationError,
      addressActionResponse: this.addressActionResponse,
      hasError,
      className: config.className,
      BusinessAccountAddress: {
        moduleProps: this.props,
        className: classnames("ms-business-account-address", config.className),
      },
      showAddOrUpdateAddress: this._renderAddOrUpdateAddress(),
    };

    return renderView(viewProps) as React.ReactElement;
  }

  public _resetAddressFields = () => {
    const addressFormat = this.addressFormat.getAddressFormat(
      this.countryRegionId
    );
    addressFormat.map((addressFormatItem) => {
      switch (addressFormatItem.displayType) {
        case AddressItemDisplayType.Input:
          this.addUpdateAddress[addressFormatItem.name] = "";
          break;
        case AddressItemDisplayType.Checkbox:
          this.addUpdateAddress[addressFormatItem.name] = false;
          break;
        default:
          switch (addressFormatItem.type) {
            case AddressItemType.ThreeLetterISORegionName:
              this.addUpdateAddress[addressFormatItem.name] =
                this.countryRegionId;
              break;
            case AddressItemType.AddressTypeValue:
              this.addUpdateAddress[addressFormatItem.name] =
                this.defaultAddressType;
              break;
            case AddressItemType.State:
              this.addUpdateAddress[addressFormatItem.name] = "";
              break;
            default:
              this.addUpdateAddress[addressFormatItem.name] = undefined;
          }
      }
    });
  };

  // Set validationError and hasError to hide error messages since in the B2B
  // request form, no errors would visually show since user wont be able to submit
  // until form is filled and correct
  private readonly _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
    return AddressAddUpdate({
      isUpdating: this.isUpdating,
      resources: this.resources,
      addressType: AddressType.Company,
      addressFormat: this.addressFormat.getAddressFormat(this.countryRegionId),
      defaultCountryRegionId: this.countryRegionId,
      defaultAddressType: this.defaultAddressType,
      selectedAddress: this.addUpdateAddress,
      validationError: this.validationError,
      hasError: this.props.hasError,
      addressActionResponse: this.addressActionResponse,
      telemetryContent: this.telemetryContent,
      dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(
        this.resources.addressStateDefaultSelectionText,
        this.stateProvinceInfo
      ),
      onInputChange: this._onAddressAddUpdateInputChange,
      onDropdownChange: this._onAddressAddUpdateDropdownChange,
    });
  };

  private readonly _onAddressAddUpdateInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this._updateAddress(event.target.name, event.target.value);
  };

  private readonly _onAddressAddUpdateDropdownChange = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ): Promise<void> => {
    if (
      event.target.name ===
      AddressItemType[AddressItemType.ThreeLetterISORegionName]
    ) {
      await this._updateCountryRegionId(event.target.value);
    } else {
      this._updateAddress(event.target.name, event.target.value);
    }
  };

  private readonly _updateAddress = (name: string, value: string | number) => {
    const { updateForm, resources } = this.props;
    let cleanValue = value;
    if (typeof value === "string") {
      cleanValue = (value || "").replace(new RegExp("[<>]", "gi"), "");
    }
    set(this.addUpdateAddress, { [name]: cleanValue });

    // if (this._isEmpty()) {
    //     if (isRequired) {
    //         updateForm(true, undefined);
    //     } else {
    //         // Reset addressFormat as if address was NOT a required field
    //         this.addressFormat = new AddressFormat(this.countryRegions, new AddressMetaData({ ...resources }, this.excludedList, []), this.addressPurposes);
    //         updateForm(false, this.addUpdateAddress);
    //     }
    // } else {
    // Get new addressFormat as if address was a required field
    this.addressFormat = new AddressFormat(
      this.countryRegions,
      new AddressMetaData({ ...resources }, this.excludedList, undefined),
      this.addressPurposes
    );

    // Verify with new rules
    if (
      this.addressFormat.validateAddressFormat(
        this.addUpdateAddress,
        this.validationError,
        this.countryRegionId
      )
    ) {
      updateForm(false, this.addUpdateAddress);
    } else {
      updateForm(true, undefined);
    }
    // }
  };

  // Walk through array and check if valued is defined or value is not an empty string
  // private readonly _isEmpty = () => {
  //     const addressFormat = this.addressFormat.getAddressFormat(this.countryRegionId);
  //     for (const addressFormatItem of addressFormat) {
  //         if (addressFormatItem.type !== AddressItemType.ThreeLetterISORegionName || this._isInputRequired(addressFormatItem)) {
  //             const addressItemValue = this.addUpdateAddress[addressFormatItem.name];

  //             // Check each input field, if value is defined and not an empty string --> address is not empty
  //             if (addressItemValue || (addressItemValue && addressItemValue !== '')) {
  //                 return false;
  //             }
  //         }
  //     }

  //     return true;
  // };

  // private readonly _isInputRequired = (addressFormatItem: IAddressItem): boolean => {

  //     if (addressFormatItem === null || !addressFormatItem.validationRules || addressFormatItem.validationRules.length === 0) {
  //         return false;
  //     }

  //     for (const validationRule of addressFormatItem.validationRules) {
  //         if (validationRule.type === AddressValidationRuleType.Required) {
  //             return false;
  //         }
  //     }

  //     return true;
  // };

  private _setDefaultCountryRegionId(): void {
    const { request } = this.props.context;
    const market =
      request && request.channel && request.channel.ChannelCountryRegionISOCode;

    this._updateCountryRegionId(
      this.addressCommon.getDefaultCountryRegionId(
        this.countryRegionId,
        this.countryRegions,
        market
      )
    );

    // Set default AddressType
    set(this.addUpdateAddress, { AddressTypeValue: this.defaultAddressType });
  }

  private readonly _updateCountryRegionId = async (value: string) => {
    this.countryRegionId = value;

    await this.addressCommon
      .getStateProvinces(this.countryRegionId)
      .then((result: StateProvinceInfo[]) => {
        const stateInfo = result.some(
          (state) => state.StateId === this.addUpdateAddress.State
        );

        // Reset state if selected state not found in the list.
        if (!stateInfo) {
          this._updateAddress("State", "");
        }

        this.stateProvinceInfo = result;
      });

    this._updateAddress("ThreeLetterISORegionName", this.countryRegionId);
  };
}

export default BusinessAccountAddress;
